import React, { useState, useEffect, useRef } from "react";
import AuctionCarCard from "./AuctionCarCard";
import placeholder from "../assets/Car place holder PNG.png";
import { useLanguage } from "../context/LanguageContext";
// import translations from "../../locals/translations.json";
import translations from "../locals/translations.json";
import { getPaginatedProducts } from "../services/vehicleServices.ts";

import { useNavigate } from "react-router-dom";
import VehicleCard from "./VehicleCard";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api.ts";

const FeaturedCars = () => {
  const navigate = useNavigate();
  const [auctionCars, setAuctionCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [wishlistData, setWishlistData] = useState([]);
  const scrollContainerRef = useRef(null);
  const { language } = useLanguage();
  const { user } = useAuth();
  const isRTL = language === "ar";

  // Utility function for safe string extraction
  const safeString = (value) => {
    if (value === null || value === undefined) return "";
    return String(value).trim();
  };

  useEffect(() => {
    const fetchAuctionCars = async () => {
      try {
        // const response = await fetch(
        //   `${process.env.REACT_APP_API_URL}/products/paginated?isFeatured=1&unSold=1=${language}`
        // );
        const response = await getPaginatedProducts({
          type: "upcomming",
          page: 1,
          limit: 50,
          order: "DESC",
          orderBy: "lotNumber",
          filters: {
            ProductStatus: "2,3,4",
            isFeatured: 1,
            unSold: 1,
          },

          language: language,
        });

        if (!response.data) {
          throw new Error("Failed to fetch auction cars");
        }
        const vehicles = await response.data;

        console.log("Vehicles", vehicles);
        // Format the cars data to match our component's expected structure using comprehensive mapping from Search.jsx
        const formattedCars = vehicles.map((product) => ({
          id: product.id,
          images: product.images,
          slug: product.slug,
          name: safeString(product.name),
          year: Number(product.year || 0),
          make: safeString(
            product.MakeModel?.make?.name || product.make?.name || ""
          ),
          model: safeString(
            product.MakeModel?.name || product.model?.name || ""
          ),
          variant: safeString(product.variant?.name || ""),
          lotNumber: product?.lotNumber,
          highlight: safeString(product.Highlight?.name || ""),
          condition: safeString(
            product.primaryDamageType?.name ||
              product.secondaryDamageType?.name ||
              product.condition?.name ||
              "-"
          ),
          highlightBgColor: product.Highlight?.bgColor,
          highlightTextColor: product.Highlight?.textColor,
          mileage: Number(product.mileage || 0),
          fuelType: safeString(product.fuelType?.name || ""),
          cylinder: safeString(product.cylinder?.name || ""),
          engineSize: safeString(product.engine?.name || ""),
          startBidding: Number(product?.startingBid || 0),
          latestBid: Number(product?.latestBid || 0),
          startingBid: Number(product.startingBid || 0), // Keep original for backward compatibility
          currentBid: Number(product?.latestBid || 0), // Keep original for backward compatibility
          auctionLaneId: (() => {
            const laneId =
              product?.auctionLanesVehicles?.[0]?.auctionLane?.id ||
              product?.auctionLanesVehicles?.[0]?.id ||
              product?.auctionLanes?.id;
            return laneId;
          })(),
          bidIncrement: Number(product?.bidIncrement || 0),
          reservedPrice: Number(product?.reservedPrice || 0),
          vinNumber: safeString(product.vinNumber || ""),
          isRunAndDrive: product.keys || false,
          productStatus: product.ProductStatus,
          buyNowPrice: Number(product.buyNowPrice || 0),

          // Additional bidding and auction fields from Search.jsx
          auctionStatus: safeString(
            product.auctionStatus || product.auction_status || ""
          ),
          estimatedRetailValue: Number(
            product.estimatedRetailValue || product.estimated_retail_value || 0
          ),
          sellerName: safeString(product?.seller?.user?.name || ""),
          vin: (() => {
            const vin = safeString(product?.vinNumber);
            if (vin && vin.length > 4) {
              return "*****" + vin.slice(-4);
            }
            return vin;
          })(),

          // Additional comprehensive fields from Search.jsx
          transmission: safeString(product.transmission?.name || ""),
          drivetrain: safeString(product.drivetrain?.name || ""),
          bodyType: safeString(product.bodyType?.name || ""),
          doors: Number(product.doors || 0),
          seats: Number(product.seats || 0),
          exteriorColor: safeString(product?.exteriorColor?.name || ""),
          interiorColor: safeString(product?.interiorColor?.name || ""),
          isFeatured: Number(product?.isFeatured || 0),
          runNo: product?.auctionLanesVehicles?.[0]?.runNo || product?.runNo,

          // Additional fields from API response
          reservedPrice: Number(product?.reservedPrice || 0),
          marketValue: Number(product?.marketValue || 0),
          
          // Pass the entire auctionLanesVehicles array
          auctionLanesVehicles: product?.auctionLanesVehicles || [],
          
          // Pass ProductStatus object (not just the name)
          ProductStatus: product?.ProductStatus,

          // Status fields
          productAuctionStatus: safeString(
            product?.ProductAuctionStatus?.name || ""
          ),
          productPaymentStatus: safeString(
            product?.ProductPaymentStatus?.name || ""
          ),
          productStatus: safeString(product?.ProductStatus?.name || ""),

          // Comprehensive highlights processing
          highlights: (() => {
            // Check different possible highlight structures
            const highlightSources = [
              product?.highlights,
              product?.Highlights,
              product?.highlight,
              product?.Highlight,
            ];

            for (const source of highlightSources) {
              if (Array.isArray(source) && source.length > 0) {
                return source
                  .map((h) => {
                    if (typeof h === "object" && h !== null) {
                      return {
                        id: h.id,
                        name: safeString(h.name),
                        textColor: h.textColor || "#ffffff",
                        bgColor: h.bgColor || "#f97316",
                      };
                    }
                    return safeString(h);
                  })
                  .filter((h) => h && (typeof h === "object" ? h.name : h));
              }
              // Handle single highlight object
              if (
                typeof source === "object" &&
                source !== null &&
                source.name
              ) {
                return [
                  {
                    id: source.id,
                    name: safeString(source.name),
                    textColor: source.textColor || "#ffffff",
                    bgColor: source.bgColor || "#f97316",
                  },
                ];
              }
            }

            return [];
          })(),
        }));
        setAuctionCars(formattedCars);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchAuctionCars();
  }, [language]);

  // Fetch wishlist data
  useEffect(() => {
    const fetchWishlist = async () => {
      if (!user?.id) {
        setWishlistData([]);
        return;
      }

      try {
        const response = await api.get("/wishlist");
        const wishlist = response.data?.data || response.data;
        
        if (wishlist && Array.isArray(wishlist)) {
          setWishlistData(wishlist);
        } else {
          setWishlistData([]);
        }
      } catch (error) {
        console.error("Error fetching wishlist:", error);
        setWishlistData([]);
      }
    };

    fetchWishlist();
  }, [user?.id]);
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const scrollAmount = container.clientWidth / 2; // Adjust this value for smoother scrolling
      container.scrollBy({
        left: -scrollAmount,
        behavior: "smooth",
      });
    }
  };
  const allVehicles = () => {
    navigate(`/vehicles?featured=1`);
  };
  const scrollRight = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const scrollAmount = container.clientWidth / 4; // Adjust this value for smoother scrolling
      container.scrollBy({
        left: scrollAmount,
        behavior: "smooth",
      });
    }
  };
  if (loading) {
    return <div>Loading auction cars...</div>;
  }
  if (error) {
    return <div>Error: {error}</div>;
  }
  return (
    <>
      <section className="max-w-7xl mx-auto py-10">
        <div dir={isRTL ? "rtl" : "ltr"} className="relative">
          <div className="text-center max-w-2xl mx-auto px-4">
            <h2 className="text-[20px] md:text-[30px] font-semibold text-center">
              {translations[language].upcomingAuction.bestCars}{" "}
              <span className="text-primary-dark">
                {translations[language].upcomingAuction.upcoming}
              </span>{" "}
              {translations[language].upcomingAuction.auction}
            </h2>
            <p className="text-[16px] mb-0">
              {translations[language].upcomingAuction.description}
            </p>
          </div>
          {/* Responsive controls */}
          {/* <div
            className='
      flex flex-col sm:flex-row items-center gap-4
      relative sm:absolute sm:top-0 sm:right-0
      justify-center sm:justify-end
      mt-4 sm:mt-0
    '
          > */}
          <div
            className={`
    flex flex-col sm:flex-row items-center gap-4
    relative sm:absolute sm:bottom-0
    ${isRTL ? "sm:left-0" : "sm:right-0"}   
    justify-end sm:justify-end
    mt-4 sm:mt-0
  `}
          >
            {/* Buttons */}
            <div className="flex gap-4">
              <button onClick={scrollLeft} className="focus:outline-none">
                {/* Left Arrow */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="56"
                  height="56"
                  viewBox="0 0 56 56"
                  fill="none"
                >
                  <g filter="url(#filter0_d_231_1183)">
                    <path
                      d="M8 14C8 8.47715 12.4772 4 18 4H38C43.5228 4 48 8.47715 48 14V34C48 39.5228 43.5228 44 38 44H18C12.4772 44 8 39.5228 8 34V14Z"
                      fill="#414042"
                      shapeRendering="crispEdges"
                    />
                    <path
                      d="M18 4.75H38C43.1086 4.75 47.25 8.89137 47.25 14V34C47.25 39.1086 43.1086 43.25 38 43.25H18C12.8914 43.25 8.75 39.1086 8.75 34V14C8.75 8.89137 12.8914 4.75 18 4.75Z"
                      stroke="#888888"
                      strokeOpacity="0.6"
                      strokeWidth="1.5"
                      shapeRendering="crispEdges"
                    />
                    <path
                      d={
                        isRTL
                          ? "M32.6337 24.8L25.8837 31.68C25.6744 31.8933 25.4215 32 25.125 32C24.8285 32 24.5669 31.8933 24.3401 31.68C24.1134 31.4667 24 31.2 24 30.88C24 30.56 24.1221 30.2933 24.3663 30.08L30.3314 24L24.3663 17.92C24.1221 17.7067 24 17.44 24 17.12C24 16.8 24.1134 16.5333 24.3401 16.32C24.5669 16.1067 24.8285 16 25.125 16C25.4215 16 25.6744 16.1067 25.8837 16.32L32.6337 23.2C32.8779 23.4133 33 23.68 33 24C33 24.32 32.8779 24.5867 32.6337 24.8Z"
                          : "M23 24C23 23.68 23.1221 23.4133 23.3663 23.2L30.1163 16.32C30.3256 16.1067 30.5785 16 30.875 16C31.1715 16 31.4331 16.1067 31.6599 16.32C31.8866 16.5333 32 16.8 32 17.12C32 17.44 31.8779 17.7067 31.6337 17.92L25.6686 24L31.6337 30.08C31.843 30.2933 31.9477 30.56 31.9477 30.88C31.9477 31.2 31.843 31.4667 31.6337 31.68C31.4244 31.8933 31.1715 32 30.875 32C30.5785 32 30.3256 31.8933 30.1163 31.68L23.3663 24.8C23.1221 24.5867 23 24.32 23 24Z"
                      }
                      fill="#FFFFFF"
                    />
                  </g>
                </svg>
              </button>

              <button onClick={scrollRight} className="focus:outline-none">
                {/* Right Arrow */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="56"
                  height="56"
                  viewBox="0 0 56 56"
                  fill="none"
                >
                  <g filter="url(#filter0_d_1347_2839)">
                    <rect
                      x="8"
                      y="4"
                      width="40"
                      height="40"
                      rx="10"
                      fill="#414042"
                    />
                    <rect
                      x="8.75"
                      y="4.75"
                      width="38.5"
                      height="38.5"
                      rx="9.25"
                      stroke="#888888"
                      strokeOpacity="0.6"
                      strokeWidth="1.5"
                    />
                    <path
                      d={
                        isRTL
                          ? "M23 24C23 23.68 23.1221 23.4133 23.3663 23.2L30.1163 16.32C30.3256 16.1067 30.5785 16 30.875 16C31.1715 16 31.4331 16.1067 31.6599 16.32C31.8866 16.5333 32 16.8 32 17.12C32 17.44 31.8779 17.7067 31.6337 17.92L25.6686 24L31.6337 30.08C31.843 30.2933 31.9477 30.56 31.9477 30.88C31.9477 31.2 31.843 31.4667 31.6337 31.68C31.4244 31.8933 31.1715 32 30.875 32C30.5785 32 30.3256 31.8933 30.1163 31.68L23.3663 24.8C23.1221 24.5867 23 24.32 23 24Z"
                          : "M32.6337 24.8L25.8837 31.68C25.6744 31.8933 25.4215 32 25.125 32C24.8285 32 24.5669 31.8933 24.3401 31.68C24.1134 31.4667 24 31.2 24 30.88C24 30.56 24.1221 30.2933 24.3663 30.08L30.3314 24L24.3663 17.92C24.1221 17.7067 24 17.44 24 17.12C24 16.8 24.1134 16.5333 24.3401 16.32C24.5669 16.1067 24.8285 16 25.125 16C25.4215 16 25.6744 16.1067 25.8837 16.32L32.6337 23.2C32.8779 23.4133 33 23.68 33 24C33 24.32 32.8779 24.5867 32.6337 24.8Z"
                      }
                      fill="#FFFFFF"
                    />
                  </g>
                </svg>
              </button>
            </div>
            {/* View All */}
            <div className="flex items-center mx-0 sm:mx-4 cursor-pointer">
              <h1 onClick={allVehicles}>
                {translations[language].upcomingAuction.viewAll}

              </h1>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="17"
                height="17"
                viewBox="0 0 17 17"
                fill="none"
              >
                <path
                  d="M16.5248 0.779351H6.17181C5.92614 0.779351 6.17181 0.533685 6.17181 0.779351C6.17181 1.02502 5.92614 1.72053 6.17181 1.72053H14.6424L0.52475 15.8382C0.351125 16.0118 0.351125 16.6058 0.52475 16.7794C0.411041 16.7794 0.611542 16.8661 0.52475 16.7794C0.638458 16.7794 1.37909 16.8661 1.46593 16.7794L15.5836 2.6617V11.1323C15.5836 11.378 16.279 11.1323 16.5248 11.1323C16.5248 11.378 16.7705 11.1323 16.5248 11.1323V0.779351C16.7704 0.779351 16.5248 0.533685 16.5248 0.779351Z"
                  fill="#DE542E"
                />
              </svg>
            </div>
          </div>
        </div>
        {/* ==================================================== */}
        <div className="py-0 relative">
          <div
            ref={scrollContainerRef}
            className="flex overflow-x-auto py-4 scrollbar-hide"
            style={{ scrollbarWidth: "none" }}
          >
            {auctionCars.map((car) => (
              <div
                key={car.id}
                className="flex-shrink-0 w-full md:w-/4 lg:w-1/4 px-2 rounded-none"
              >
                {/* <AuctionCarCard car={car} /> */}
                <VehicleCard vehicle={car} key={car.id} wishlistData={wishlistData} />
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};
export default FeaturedCars;
